/**
 * Custom listbox dropdown that enhances a native <select>, plus Brand → model
 * (and high/low) shop filters that filter via collection tag URLs.
 *
 * The brand and model lists come from assets/fp-filter-index.json, built from
 * the products' own tags by scripts/build_filter_index.py, so every choice has
 * products behind it. Re-run that script after adding seat covers.
 *
 * In the phone filter drawer a choice does not reload the page (that closed
 * the drawer after every pick): choices collect, and "Show results" (or the
 * drawer's own results button) applies them together. On the desktop rail a
 * choice applies straight away.
 */

// One request for the index, shared by every filter bar on the page.
const fpFilterIndex = (url) => {
  if (!window.fpFilterIndexPromise) {
    window.fpFilterIndexPromise = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : {}))
      .catch(() => {
        window.fpFilterIndexPromise = undefined;
        return {};
      });
  }
  return window.fpFilterIndexPromise;
};

// Reading a vehicle from search words (see FpShopFilters#vehicleFromSearch).
// Every machine category has high-back and low-back covers; lorries, pickups
// and vans are chosen by seat instead.
const FP_NO_FIT_CATALOGUES = ['all', 'lorries', 'pickups', 'vans'];
const fpHasFit = (catalogue) => !FP_NO_FIT_CATALOGUES.includes(catalogue);
// The original eight; newer categories come from data-new-catalogues
// (snippets/fp-new-catalogues.liquid), and only once their collection is live.
const FP_CORE_CATALOGUES = ['tractors', 'lorries', 'excavators', 'loaders', 'telehandlers', 'forklifts', 'pickups', 'vans'];
const FP_TYPE_WORDS = {
  tractors: ['tractor', 'tractors'],
  lorries: ['lorry', 'lorries', 'truck', 'trucks', 'hgv'],
  excavators: ['excavator', 'excavators', 'digger', 'diggers'],
  loaders: ['loader', 'loaders', 'dumper', 'dumpers'],
  telehandlers: ['telehandler', 'telehandlers'],
  forklifts: ['forklift', 'forklifts', 'fork lift'],
  pickups: ['pickup', 'pickups', 'pick up', '4x4'],
  vans: ['van', 'vans'],
};
const FP_BRAND_ALIASES = {
  'Massey Ferguson': ['mf', 'massey'],
  'John Deere': ['jd', 'deere'],
  'New Holland': ['nh'],
  'Case IH': ['caseih'],
  'Deutz-Fahr': ['deutz'],
  Caterpillar: ['cat'],
  'Volvo CE': ['volvo'],
  'Volvo Trucks': ['volvo'],
  'Renault Trucks': ['renault'],
  'Mercedes-Benz': ['mercedes', 'merc'],
  Volkswagen: ['vw'],
  'Land Rover': ['landrover'],
  'Toyota Material Handling': ['toyota'],
  'Mitsubishi Forklift Trucks': ['mitsubishi'],
};
// Words in model names that customers rarely type.
const FP_MODEL_FILLER = new Set(['series', 'compact', 'sub', 'utility', 'specialist', 'articulated', 'based', 'range', 'models', 'and']);

// Lower case, accents off, punctuation to spaces, padded so ` word ` matches whole words.
const fpWords = (value) =>
  ` ${String(value || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()} `;

// How well a list entry matches what was typed, or null for no match. Every
// typed word has to be in the entry, brackets and dashes aside, so "transit
// 2014" and "2014-" both find "Transit (2014–present)". Lower ranks first:
// exact, starts with the text, every word starts a word, anywhere; then how
// early the words sit, so "2014" puts 2014–present ahead of 2006–2014.
const fpMatchRank = (text, query) => {
  const hay = fpWords(text);
  const words = fpWords(query).trim().split(' ').filter(Boolean);
  if (!words.length) return [0, 0];
  let spread = 0;
  let wholeWords = true;
  for (const word of words) {
    const at = hay.indexOf(word);
    if (at < 0) return null;
    spread += at;
    if (!hay.includes(` ${word}`)) wholeWords = false;
  }
  const needle = words.join(' ');
  const tier = hay.trim() === needle ? 0 : hay.startsWith(` ${needle}`) ? 1 : wholeWords ? 2 : 3;
  return [tier, spread];
};

// "T4 F / N / V specialist (2021–present)" -> ['t4']. Years in brackets and
// single-letter variants are left out; a leading letter ("E-Transit") stays.
const fpModelWords = (label) =>
  fpWords(String(label).replace(/\([^)]*\)/g, ' '))
    .trim()
    .split(' ')
    .filter((word, index) => word && !FP_MODEL_FILLER.has(word) && (index === 0 || word.length > 1));

// Does "(2012–2023)", "(2023–present)" or "(from 2009)" in a label cover this year?
const fpYearInLabel = (label, year) => {
  const range = String(label).match(/\((?:from\s+)?(\d{4})(?:\s*[–-]\s*(\d{4}|present))?\)/i);
  if (!range) return false;
  const start = Number(range[1]);
  const end = range[2] ? (/present/i.test(range[2]) ? 9999 : Number(range[2])) : /from/i.test(range[0]) ? 9999 : start;
  return year >= start && year <= end;
};

const FP_CARET =
  '<svg viewBox="0 0 12 8" fill="none" aria-hidden="true" focusable="false"><path d="M1 1.5l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class FpDropdown extends HTMLElement {
  #id = '';
  #activeIndex = 0;
  // The option list is only built when the dropdown is first opened. Building
  // it up front made a button per option for every dropdown on every page
  // load (283 for tractor models alone), and rebuilt them each time the
  // options changed: about 0.4s of a mid-range phone's time on a collection.
  #listDirty = true;
  /** @type {MutationObserver | undefined} */
  #observer;
  #boundDocClick = (event) => this.#onDocClick(event);
  #boundDocKey = (event) => this.#onDocKey(event);

  connectedCallback() {
    this.select = this.querySelector('select');
    if (!(this.select instanceof HTMLSelectElement) || this.trigger) return;

    this.#id = `fp-dd-${Math.random().toString(36).slice(2, 9)}`;
    this.#activeIndex = 0;

    this.select.classList.add('visually-hidden');
    this.select.setAttribute('tabindex', '-1');
    this.select.setAttribute('aria-hidden', 'true');

    this.#build();
    this.#syncFromSelect();
    this.#observer = new MutationObserver(() => this.#rebuild());
    this.#observer.observe(this.select, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    this.select.addEventListener('change', () => this.#syncFromSelect());
    document.addEventListener('click', this.#boundDocClick);
    document.addEventListener('keydown', this.#boundDocKey);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener('click', this.#boundDocClick);
    document.removeEventListener('keydown', this.#boundDocKey);
  }

  open() {
    if (this.select?.disabled || this.isOpen) return;
    document.querySelectorAll('fp-dropdown.is-open').forEach((el) => {
      if (el !== this) el.close();
    });
    if (this.#listDirty) this.#fillList();
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
    this.#activeIndex = Math.max(
      0,
      this.#visibleOptions().findIndex((btn) => btn.classList.contains('is-selected'))
    );
    this.#paintActive();
    if (this.search) {
      this.search.value = '';
      this.#filter('');
      requestAnimationFrame(() => this.search.focus());
    } else {
      this.trigger.focus();
    }
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    if (this.search) {
      this.search.value = '';
      this.#filter('');
    }
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  // For a value set from script, which fires no change event.
  refresh() {
    this.#syncFromSelect();
  }

  // Build again after a redraw removed the button and list (see FpShopFilters#heal).
  rebuild() {
    this.disconnectedCallback();
    this.querySelectorAll(':scope > .fp-dropdown__trigger, :scope > .fp-dropdown__panel').forEach((node) => node.remove());
    this.trigger = undefined;
    this.#listDirty = true;
    this.connectedCallback();
  }

  #build() {
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'fp-dropdown__trigger';
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', `${this.#id}-list`);
    this.trigger.innerHTML = `<span class="fp-dropdown__label"></span><span class="fp-dropdown__caret">${FP_CARET}</span>`;
    this.labelEl = this.trigger.querySelector('.fp-dropdown__label');
    this.trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
    // The button shows the chosen value, and the field's visible label says
    // what it filters. An `aria-label` of "Filter by brand" replaced both, so
    // the accessible name no longer contained the words printed on the
    // button — the words someone using voice control would actually say.
    // Building the name from the two visible pieces keeps the context and
    // keeps the words, and follows the value as it changes.
    this.labelEl.id = `${this.#id}-value`;
    const field = this.closest('.fp-shop-filters__field');
    const fieldLabel = field?.querySelector('.fp-shop-filters__label');
    if (fieldLabel && !fieldLabel.id) fieldLabel.id = `${this.#id}-field`;
    this.trigger.setAttribute(
      'aria-labelledby',
      fieldLabel ? `${fieldLabel.id} ${this.labelEl.id}` : this.labelEl.id
    );

    this.panel = document.createElement('div');
    this.panel.className = 'fp-dropdown__panel';
    this.panel.hidden = true;

    if (this.hasAttribute('data-searchable')) {
      this.search = document.createElement('input');
      this.search.type = 'search';
      this.search.className = 'fp-dropdown__search';
      this.search.placeholder = this.getAttribute('data-search-placeholder') || 'Search';
      this.search.setAttribute('autocomplete', 'off');
      this.search.setAttribute('aria-label', this.search.placeholder);
      this.search.addEventListener('input', () => this.#filter(this.search.value));
      this.search.addEventListener('keydown', (event) => this.#onListKey(event));
      this.panel.append(this.search);
    }

    this.list = document.createElement('ul');
    this.list.className = 'fp-dropdown__list';
    this.list.id = `${this.#id}-list`;
    this.list.setAttribute('role', 'listbox');
    this.list.addEventListener('click', (event) => {
      const button = event.target.closest('[data-value]');
      if (button) this.#choose(button.getAttribute('data-value') || '');
    });
    this.panel.append(this.list);

    this.empty = document.createElement('p');
    this.empty.className = 'fp-dropdown__empty';
    this.empty.hidden = true;
    this.empty.textContent = 'No matches';
    this.panel.append(this.empty);

    this.append(this.trigger, this.panel);
    this.#rebuild();
  }

  #rebuild() {
    if (!this.list || !this.select) return;
    this.trigger.disabled = this.select.disabled;
    this.classList.toggle('is-disabled', this.select.disabled);
    if (!this.isOpen) {
      // Closed: just remember to build the list when it is next opened.
      this.#listDirty = true;
      this.list.replaceChildren();
      this.#syncFromSelect();
      return;
    }
    this.#fillList();
  }

  #fillList() {
    this.#listDirty = false;
    this.list.replaceChildren();
    const fragment = document.createDocumentFragment();
    Array.from(this.select.options).forEach((option) => {
      // Options with no products for the current brand and model are disabled.
      if (option.disabled) return;
      const item = document.createElement('li');
      item.className = 'fp-dropdown__item';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fp-dropdown__option';
      button.setAttribute('role', 'option');
      button.setAttribute('data-value', option.value);
      button.textContent = option.textContent || option.value;
      item.append(button);
      fragment.append(item);
    });
    this.list.append(fragment);
    this.#syncFromSelect();
    if (this.search) this.#filter(this.search.value);
  }

  #syncFromSelect() {
    if (!this.select || !this.labelEl) return;
    const selected = this.select.selectedOptions[0];
    const text = selected?.textContent || '';
    this.labelEl.textContent = text;
    this.trigger.classList.toggle('has-value', Boolean(this.select.value) && this.select.value !== 'all');
    this.list?.querySelectorAll('[data-value]').forEach((button) => {
      const on = button.getAttribute('data-value') === this.select.value;
      button.classList.toggle('is-selected', on);
      button.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  #choose(value) {
    if (!this.select || this.select.value === value) {
      this.close();
      this.trigger.focus();
      return;
    }
    this.select.value = value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.#syncFromSelect();
    this.close();
    this.trigger.focus();
  }

  #filter(query) {
    const needle = String(query || '').trim();
    let visible = 0;
    // Enter takes the closest match (fpMatchRank), not the first one that
    // contains the words: "transit custom" means Transit Custom, not
    // E-Transit Custom.
    let best = 0;
    let bestRank = null;
    this.list.querySelectorAll('.fp-dropdown__item').forEach((item) => {
      const button = item.querySelector('[data-value]');
      const rank = needle ? fpMatchRank(button?.textContent || '', needle) : [0, 0];
      item.hidden = !rank;
      if (!rank) return;
      if (needle && (!bestRank || rank[0] < bestRank[0] || (rank[0] === bestRank[0] && rank[1] < bestRank[1]))) {
        bestRank = rank;
        best = visible;
      }
      visible += 1;
    });
    this.empty.hidden = visible > 0;
    this.#activeIndex = best;
    this.#paintActive();
  }

  #visibleOptions() {
    return Array.from(this.list.querySelectorAll('.fp-dropdown__item:not([hidden]) [data-value]'));
  }

  #paintActive() {
    const options = this.#visibleOptions();
    options.forEach((button, index) => button.classList.toggle('is-active', index === this.#activeIndex));
    options[this.#activeIndex]?.scrollIntoView({ block: 'nearest' });
  }

  #onListKey(event) {
    const options = this.#visibleOptions();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#activeIndex = Math.min(options.length - 1, this.#activeIndex + 1);
      this.#paintActive();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#activeIndex = Math.max(0, this.#activeIndex - 1);
      this.#paintActive();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.#activeIndex = 0;
      this.#paintActive();
    } else if (event.key === 'End') {
      event.preventDefault();
      this.#activeIndex = Math.max(0, options.length - 1);
      this.#paintActive();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const value = options[this.#activeIndex]?.getAttribute('data-value');
      if (value != null) this.#choose(value);
    }
  }

  #onDocClick(event) {
    if (!this.isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.close();
  }

  #onDocKey(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.trigger.focus();
      return;
    }
    if (event.target === this.search) return;
    if (event.target === this.trigger) this.#onListKey(event);
  }
}

class FpShopFilters extends HTMLElement {
  async connectedCallback() {
    this.drawer = this.closest('dialog');
    if (!this.listening) {
      this.listening = true;
      // Listen on the bar, not on each select: a price or sort change redraws
      // the filters, and the selects may be new elements afterwards.
      this.addEventListener('change', (event) => this.#onSelectChange(event));
      this.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('[data-fp-filter-apply]')) this.#apply();
      });
      // The drawer's own "See n items" button applies pending choices too.
      this.drawer?.addEventListener('click', (event) => {
        if (this.pending && event.target instanceof Element && event.target.closest('.facets__see-results')) this.#apply();
      });
      // Closed with the cross instead: forget the waiting choices. Watching
      // the dialog's open attribute catches every way it closes.
      if (this.drawer) {
        new MutationObserver(() => {
          if (!this.drawer.open) this.#discardPending();
        }).observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
      }
      this.homeCatalogue = this.dataset.catalogue;
      // Back to this page from the browser's cache after applying: the
      // choices on show are the ones that were applied elsewhere.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.applying = false;
        this.#discardPending();
      });
    }

    if (!this.catalogues) {
      // The lists are fetched, not inlined -- wait before building the selects.
      const index = await fpFilterIndex(this.dataset.indexUrl);
      // Then wait for a quiet moment, so filling the brand and model lists never
      // competes with the products and photos drawing on a phone.
      await new Promise((resolve) => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(() => resolve(), { timeout: 1500 });
        else setTimeout(resolve, 1);
      });
      if (!this.isConnected) return;
      this.catalogues = this.#loadCatalogues(index);
    }
    this.#setup();

    // A price, stock or sort change redraws this bar from the server's markup
    // (Horizon morphs the section): the dropdown buttons and the brand list
    // vanish while this element stays, so connectedCallback never runs again.
    // Rebuild when that happens. Without it the bar showed its labels with no
    // usable choices until a full refresh.
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => {
      if (this.healQueued) return;
      this.healQueued = true;
      requestAnimationFrame(() => {
        this.healQueued = false;
        this.#heal();
      });
    });
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'data-active-tags'] });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = undefined;
  }

  #heal() {
    if (!this.catalogues) return;
    const dropdowns = Array.from(this.querySelectorAll('fp-dropdown'));
    const brokenDropdowns = dropdowns.some((dropdown) => !dropdown.querySelector(':scope > .fp-dropdown__trigger'));
    const brand = this.querySelector('[data-fp-filter="brand"]');
    const emptyBrands = brand instanceof HTMLSelectElement && brand.options.length <= 1 && this.#brandsFor(this.#activeCatalogue()).length > 0;
    if (brokenDropdowns || emptyBrands || brand !== this.brandSelect) {
      dropdowns.forEach((dropdown) => dropdown.rebuild?.());
      this.#setup();
      return;
    }
    // The redraw also brings back the server's `hidden` on special collections.
    const hide = this.#hideBar();
    if (this.hidden !== hide) this.hidden = hide;
  }

  #setup() {
    this.brandSelect = this.querySelector('[data-fp-filter="brand"]');
    this.modelSelect = this.querySelector('[data-fp-filter="model"]');
    this.fitSelect = this.querySelector('[data-fp-filter="fit"]');
    this.seatSelect = this.querySelector('[data-fp-filter="seat"]');
    this.typeSelect = this.querySelector('[data-fp-filter="type"]');
    this.fitField = this.querySelector('[data-fp-fit-field]');
    this.seatField = this.querySelector('[data-fp-seat-field]');
    this.clearLink = this.querySelector('[data-fp-filter-clear]');
    this.applyButton = this.querySelector('[data-fp-filter-apply]');
    if (!(this.brandSelect instanceof HTMLSelectElement) || !(this.modelSelect instanceof HTMLSelectElement)) return;
    this.pending = false;
    this.scopeTags = this.dataset.scopeTags ? new Set(this.dataset.scopeTags.split('|')) : null;
    this.hidden = this.#hideBar();
    this.#syncFromUrl();
    this.#renderBrands();
    this.#renderModels();
    this.#limitOptions();
    this.#toggleFit();
    this.#toggleSeat();
    this.#toggleClear();
    this.brandSelect.dataset.pending = '';
    this.querySelectorAll('fp-dropdown').forEach((dropdown) => dropdown.refresh?.());
  }

  #onSelectChange(event) {
    if (!this.catalogues || !(event.target instanceof HTMLSelectElement)) return;
    switch (event.target.dataset.fpFilter) {
      case 'brand':
        this.#onBrandChange();
        break;
      case 'model':
        this.#limitOptions();
        this.#changed();
        break;
      case 'fit':
      case 'seat':
        this.#changed();
        break;
      case 'type':
        this.#onTypeChange();
        break;
    }
  }

  // Special collections holding no vehicle brands (kits, bundles) have nothing
  // for the vehicle filters to narrow.
  #hideBar() {
    return Boolean(this.scopeTags) && this.#brandsFor('all').length === 0;
  }

  // Desktop rail: apply now. Phone drawer: wait for Show results.
  #changed() {
    if (!this.drawer) {
      this.#apply();
      return;
    }
    this.pending = true;
    this.#toggleClear();
    if (this.applyButton) this.applyButton.hidden = false;
    // The drawer's own button counts the page as it is now ("See 451 items"),
    // not what the waiting choice will show.
    const seeResults = this.drawer.querySelector('.facets__see-results');
    if (seeResults) {
      seeResults.dataset.fpCount ??= seeResults.textContent;
      seeResults.textContent = 'Show results';
    }
  }

  // Phone drawer closed without Show results: put the choices back to what the
  // page shows, or the drawer reopens on a brand that was never applied
  // (Volvo CE over JCB results).
  #discardPending() {
    if (!this.pending || this.applying) return;
    this.dataset.catalogue = this.homeCatalogue;
    if (this.typeSelect) {
      const shown = Array.from(this.typeSelect.options).find((option) => option.defaultSelected);
      this.typeSelect.value = shown ? shown.value : 'all';
    }
    if (this.applyButton) this.applyButton.hidden = true;
    const seeResults = this.drawer?.querySelector('.facets__see-results');
    if (seeResults?.dataset.fpCount) seeResults.textContent = seeResults.dataset.fpCount;
    this.#setup();
  }

  #loadCatalogues(index) {
    /** @type {Record<string, {brand: string, models: {v: string, l: string}[]}[]>} */
    const catalogues = {};
    const source = index ?? {};
    const live = new Set([...FP_CORE_CATALOGUES, ...this.#newCatalogues.map((entry) => entry.key)]);
    for (const [key, entry] of Object.entries(source)) {
      // A category whose collection is still hidden would send a brand choice
      // to products nobody can see.
      if (!live.has(key)) continue;
      catalogues[key] = Array.isArray(entry?.brands) ? entry.brands : [];
    }
    return catalogues;
  }

  /** @returns {{key: string, url: string, label: string, words: string[]}[]} */
  get #newCatalogues() {
    if (!this.newCatalogueList) {
      try {
        this.newCatalogueList = JSON.parse(this.dataset.newCatalogues || '[]');
      } catch {
        this.newCatalogueList = [];
      }
      this.newCatalogueList.forEach((entry) => {
        if (!FP_TYPE_WORDS[entry.key]) FP_TYPE_WORDS[entry.key] = entry.words || [];
      });
    }
    return this.newCatalogueList;
  }

  #lockedCatalogue() {
    const locked = this.dataset.catalogue || 'all';
    return locked === 'all' ? '' : locked;
  }

  #activeCatalogue() {
    return this.#lockedCatalogue() || this.typeSelect?.value || 'all';
  }

  // In a special collection, only the brands and models it holds.
  #brandsFor(catalogue) {
    const brands = this.#allBrandsFor(catalogue);
    const scope = this.scopeTags;
    if (!scope) return brands;
    return brands
      .filter((entry) => scope.has(entry.brand))
      .map((entry) => ({ ...entry, models: (entry.models || []).filter((model) => scope.has(model.v)) }));
  }

  #allBrandsFor(catalogue) {
    if (catalogue && catalogue !== 'all') return this.catalogues[catalogue] || [];
    const merged = [];
    const seen = new Set();
    Object.keys(this.catalogues).forEach((key) => {
      (this.catalogues[key] || []).forEach((entry) => {
        if (seen.has(entry.brand)) {
          const existing = merged.find((item) => item.brand === entry.brand);
          (entry.models || []).forEach((model) => {
            if (existing && !existing.models.some((item) => item.v === model.v)) existing.models.push(model);
          });
          return;
        }
        seen.add(entry.brand);
        merged.push({ brand: entry.brand, models: [...(entry.models || [])] });
      });
    });
    merged.sort((a, b) => a.brand.localeCompare(b.brand));
    return merged;
  }

  #handleize(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #activeTags() {
    return (this.dataset.activeTags || '')
      .split('|')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  #findTag(candidates) {
    const active = this.#activeTags().map((tag) => this.#handleize(tag));
    return candidates.find((name) => active.includes(this.#handleize(name))) || '';
  }

  #syncFromUrl() {
    if (this.dataset.searchTerms && this.#activeTags().length === 0) {
      this.#syncFromSearch(this.dataset.searchTerms);
      return;
    }
    const brands = this.#brandsFor(this.#activeCatalogue());
    const brandNames = brands.map((entry) => entry.brand);
    const brand = this.#findTag(brandNames);
    const models = (brands.find((entry) => entry.brand === brand)?.models || []).map((item) => item.v);
    const model = this.#findTag(models);
    const fit = this.#findTag(['High Back', 'Low Back']);
    const seat = this.#findTag(['Driver', 'Passenger', 'Double Passenger']);

    if (this.typeSelect && this.#lockedCatalogue() === '') {
      this.typeSelect.value = this.#guessTypeFromCollection() || 'all';
    }
    this.brandSelect.dataset.pending = brand;
    this.modelSelect.dataset.pending = model;
    if (this.fitSelect) this.fitSelect.value = fit;
    if (this.seatSelect) this.seatSelect.value = seat;
  }

  // Search results: Shopify search takes no tag filters, so a choice here
  // leaves the search for a collection. Preselecting the vehicle named in the
  // search ("john deere 6r high back") makes that collection the same vehicle's
  // covers. Without it, a single choice opened a whole category, and the
  // results looked random.
  #syncFromSearch(terms) {
    const found = this.#vehicleFromSearch(terms);
    if (this.typeSelect && this.#lockedCatalogue() === '') {
      this.typeSelect.value = found.catalogue || 'all';
    }
    const catalogue = this.#activeCatalogue();
    this.brandSelect.dataset.pending = found.brand;
    this.modelSelect.dataset.pending = found.model;
    if (this.fitSelect) this.fitSelect.value = fpHasFit(catalogue) ? found.fit : '';
    if (this.seatSelect) this.seatSelect.value = catalogue === 'pickups' || catalogue === 'vans' ? found.seat : '';
  }

  #vehicleFromSearch(terms) {
    const query = fpWords(terms);
    const has = (phrase) => phrase.trim() !== '' && query.includes(phrase);
    const found = { catalogue: '', brand: '', model: '', fit: '', seat: '' };

    // A vehicle type in the search ("kubota excavator") narrows the rest.
    const typed = Object.keys(FP_TYPE_WORDS).filter((key) => FP_TYPE_WORDS[key].some((word) => has(` ${word} `)));
    const locked = this.#lockedCatalogue();
    const catalogues = locked ? [locked] : typed.length === 1 ? typed : Object.keys(this.catalogues);

    // Longest brand name wins ("Renault Trucks" over "Renault"). Brands that
    // tie ("volvo" for Volvo CE and Volvo Trucks) are settled by the model.
    let brands = [];
    let longest = 0;
    catalogues.forEach((key) => {
      (this.catalogues[key] || []).forEach((entry) => {
        [entry.brand, ...(FP_BRAND_ALIASES[entry.brand] || [])].forEach((name) => {
          const phrase = fpWords(name);
          if (!has(phrase) || phrase.length < longest) return;
          if (phrase.length > longest) brands = [];
          longest = phrase.length;
          if (!brands.includes(entry.brand)) brands.push(entry.brand);
        });
      });
    });

    // A model matches when every word of its name is in the search. The most
    // specific name wins ("6R Series", not "6RC Series"; "Transit Custom" over
    // "Transit"). Generations of one model are told apart by a year in the
    // search, or left for the customer to pick.
    let matches = [];
    let best = 0;
    catalogues.forEach((key) => {
      (this.catalogues[key] || []).forEach((entry) => {
        if (brands.length && !brands.includes(entry.brand)) return;
        (entry.models || []).forEach((model) => {
          const words = fpModelWords(model.l || model.v);
          if (!words.length || !words.every((word) => has(` ${word} `)) || words.length < best) return;
          if (words.length > best) matches = [];
          best = words.length;
          matches.push({ catalogue: key, brand: entry.brand, model: model.v, label: model.l || model.v });
        });
      });
    });
    const years = query.split(' ').filter((word) => /^(19|20)\d\d$/.test(word)).map(Number);
    if (years.length && matches.length > 1) {
      const dated = matches.filter((match) => years.some((year) => fpYearInLabel(match.label, year)));
      if (dated.length) matches = dated;
    }

    const matchBrands = [...new Set(matches.map((match) => match.brand))];
    const matchModels = [...new Set(matches.map((match) => match.model))];
    if (matchBrands.length === 1) {
      found.brand = matchBrands[0];
      if (matchModels.length === 1) found.model = matchModels[0];
    } else if (brands.length === 1) {
      found.brand = brands[0];
    }

    // Several generations of one van still make it a van.
    const matchCatalogues = [...new Set(matches.map((match) => match.catalogue))];
    if (typed.length === 1) {
      found.catalogue = typed[0];
    } else if (found.brand && matchBrands.length === 1 && matchCatalogues.length === 1) {
      found.catalogue = matchCatalogues[0];
    } else if (found.brand) {
      found.catalogue = this.#homeCatalogue(found.brand, found.model);
    }

    if (has(' high back ') || has(' highback ')) found.fit = 'High Back';
    else if (has(' low back ') || has(' lowback ')) found.fit = 'Low Back';
    if (has(' double passenger ') || has(' double ')) found.seat = 'Double Passenger';
    else if (has(' passenger ')) found.seat = found.catalogue === 'vans' ? 'Single Passenger' : 'Passenger';
    else if (has(' driver ')) found.seat = 'Driver';
    return found;
  }

  // The one type that has this brand (and model), or '' when several do.
  #homeCatalogue(brand, model) {
    const homes = Object.keys(this.catalogues).filter((key) =>
      (this.catalogues[key] || []).some(
        (entry) => entry.brand === brand && (!model || (entry.models || []).some((item) => item.v === model))
      )
    );
    return homes.length === 1 ? homes[0] : '';
  }

  #guessTypeFromCollection() {
    const url = this.dataset.collectionUrl || '';
    const newer = this.#newCatalogues.find((entry) => entry.url && url.endsWith(entry.url));
    if (newer) return newer.key;
    if (url.includes('/lorries')) return 'lorries';
    if (url.includes('/excavators')) return 'excavators';
    if (url.includes('/loaders')) return 'loaders';
    if (url.includes('/telehandler')) return 'telehandlers';
    if (url.includes('/forklift')) return 'forklifts';
    if (url.includes('/pick-up') || url.includes('/pickup') || url.includes('/4x4')) return 'pickups';
    if (url.includes('/van-seat') || url.includes('/vans')) return 'vans';
    if (url.includes('/tractor')) return 'tractors';
    return 'all';
  }

  #renderBrands() {
    const brands = this.#brandsFor(this.#activeCatalogue());
    const selected = this.brandSelect.dataset.pending || '';
    this.brandSelect.innerHTML = '<option value="">All brands</option>';
    brands.forEach((entry) => {
      const option = document.createElement('option');
      option.value = entry.brand;
      option.textContent = entry.brand;
      if (entry.brand === selected) option.selected = true;
      this.brandSelect.append(option);
    });
  }

  #renderModels() {
    const brand = this.brandSelect.value;
    const brands = this.#brandsFor(this.#activeCatalogue());
    const models = brands.find((entry) => entry.brand === brand)?.models || [];
    const selected = this.modelSelect.dataset.pending || '';
    this.modelSelect.innerHTML = '<option value="">All models</option>';
    models.forEach((model) => {
      const option = document.createElement('option');
      option.value = model.v;
      option.textContent = model.l;
      if (model.v === selected) option.selected = true;
      this.modelSelect.append(option);
    });
    this.modelSelect.disabled = !brand;
    this.modelSelect.dataset.pending = '';
  }

  #toggleFit() {
    const catalogue = this.#activeCatalogue();
    const show = fpHasFit(catalogue);
    if (this.fitField instanceof HTMLElement) this.fitField.hidden = !show;
  }

  #toggleSeat() {
    const show = this.#activeCatalogue() === 'pickups' || this.#activeCatalogue() === 'vans';
    if (this.seatField instanceof HTMLElement) this.seatField.hidden = !show;
  }

  #toggleClear() {
    // Search results aren't filtered by the bar, so there is nothing to clear.
    if (this.dataset.searchTerms) {
      if (this.clearLink instanceof HTMLAnchorElement) this.clearLink.hidden = true;
      return;
    }
    const has =
      Boolean(this.brandSelect.value) ||
      Boolean(this.modelSelect.value) ||
      Boolean(this.fitSelect?.value) ||
      Boolean(this.seatSelect?.value) ||
      (this.typeSelect && this.typeSelect.value && this.typeSelect.value !== 'all' && this.typeSelect.value !== this.#lockedCatalogue());
    if (this.clearLink instanceof HTMLAnchorElement) {
      this.clearLink.hidden = !has;
      const base = this.dataset.collectionUrl || this.#collectionUrlFor(this.#activeCatalogue());
      if (base) this.clearLink.href = this.#withSort(base);
    }
  }

  #collectionUrlFor(catalogue) {
    if (catalogue === 'tractors') return this.dataset.urlTractors || '';
    if (catalogue === 'lorries') return this.dataset.urlLorries || '';
    if (catalogue === 'excavators') return this.dataset.urlExcavators || '';
    if (catalogue === 'loaders') return this.dataset.urlLoaders || '';
    if (catalogue === 'telehandlers') return this.dataset.urlTelehandlers || '';
    if (catalogue === 'forklifts') return this.dataset.urlForklifts || '';
    if (catalogue === 'pickups') return this.dataset.urlPickups || '';
    if (catalogue === 'vans') return this.dataset.urlVans || '';
    const newer = this.#newCatalogues.find((entry) => entry.key === catalogue);
    if (newer) return newer.url;
    return this.dataset.urlAll || this.dataset.collectionUrl || '';
  }

  #withSort(url) {
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    if (!sort || !url) return url;
    const next = new URL(url, window.location.origin);
    next.searchParams.set('sort_by', sort);
    return `${next.pathname}${next.search}`;
  }

  // A vehicle choice keeps the price, stock and sort already set (it used to
  // keep only the sort, so a £35 limit vanished when a brand was picked). The
  // page number is dropped: a new result set starts at page 1.
  #withState(url) {
    if (!url) return url;
    const next = new URL(url, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key === 'sort_by' || key.startsWith('filter.')) next.searchParams.append(key, value);
    });
    return `${next.pathname}${next.search}`;
  }

  #onTypeChange() {
    const catalogue = this.typeSelect?.value || 'all';
    const url = this.#collectionUrlFor(catalogue);
    if (!url) return;
    // Inside a category, choosing another vehicle unlocks the bar, so the
    // brand list, Fit, Seat and the move all follow the Type now chosen.
    if (this.#lockedCatalogue() && catalogue !== this.#lockedCatalogue()) this.dataset.catalogue = 'all';
    // Keep the brand and model when the new type has them, so changing the
    // type narrows the same vehicle instead of starting again.
    this.brandSelect.dataset.pending = this.brandSelect.value;
    this.modelSelect.dataset.pending = this.modelSelect.value;
    this.#renderBrands();
    this.brandSelect.dataset.pending = '';
    this.#renderModels();
    this.#limitOptions();
    this.#toggleFit();
    this.#toggleSeat();
    // Drawer: Show results goes there. Rail: go now.
    this.#changed();
  }

  #onBrandChange() {
    this.modelSelect.dataset.pending = '';
    this.#renderModels();
    this.#limitOptions();
    this.#changed();
  }

  // Fit and Seat offer only what the chosen brand (and model) has products
  // for, so no combination leads to an empty page. A choice that no longer
  // fits is cleared.
  #limitOptions() {
    const entry = this.#brandsFor(this.#activeCatalogue()).find((item) => item.brand === this.brandSelect.value);
    let allowed = null;
    if (entry) {
      const chosen = this.modelSelect.value;
      const models = chosen ? entry.models.filter((model) => model.v === chosen) : entry.models;
      allowed = new Set(models.flatMap((model) => model.o || []));
    }
    // Vans split passenger seats into single and double; pickups have one kind.
    // (Double-passenger van covers also carry the plain "Passenger" tag.)
    const catalogue = this.#activeCatalogue();
    const notHere = catalogue === 'vans' ? ['Passenger'] : catalogue === 'pickups' ? ['Single Passenger', 'Double Passenger'] : [];
    [this.fitSelect, this.seatSelect].forEach((select) => {
      if (!select) return;
      Array.from(select.options).forEach((option) => {
        if (!option.value) return;
        const available = (!allowed || allowed.has(option.value)) && !notHere.includes(option.value);
        option.disabled = !available;
        option.hidden = !available;
      });
      // The dropdown redraws (and relabels) itself when options change.
      if (select.selectedOptions[0]?.disabled) select.value = '';
    });
  }

  #apply() {
    let catalogue = this.#activeCatalogue();
    // A special collection (Branded seat covers) filters inside itself unless
    // the customer picks a Type, which is a deliberate move to that range.
    const scoped = this.hasAttribute('data-scoped') && catalogue === 'all';
    // "All products" with a brand chosen: use that brand's own type when it
    // has just one, so the page and its Fit and Seat choices match.
    if (catalogue === 'all' && this.brandSelect.value && !scoped) {
      catalogue = this.#homeCatalogue(this.brandSelect.value, this.modelSelect.value) || 'all';
    }
    const base = this.#lockedCatalogue() || scoped
      ? this.dataset.collectionUrl || this.#collectionUrlFor(catalogue)
      : this.#collectionUrlFor(catalogue);
    if (!base) return;

    const tags = [];
    if (this.brandSelect.value) tags.push(this.brandSelect.value);
    if (this.modelSelect.value) tags.push(this.modelSelect.value);
    if (this.fitSelect?.value && catalogue !== 'lorries' && catalogue !== 'pickups' && catalogue !== 'vans') tags.push(this.fitSelect.value);
    if (this.seatSelect?.value && (catalogue === 'pickups' || catalogue === 'vans')) tags.push(this.seatSelect.value);

    const path = tags.map((tag) => this.#handleize(tag)).filter(Boolean).join('+');
    const next = this.#withState(path ? `${base.replace(/\/$/, '')}/${path}` : base);
    const current = `${window.location.pathname}${window.location.search}`;
    if (next === current || next === window.location.pathname) {
      this.#toggleClear();
      return;
    }
    // Only once the navigation is certain: bailing out above must leave the
    // grid it already has.
    window.fpAnalytics?.track('collection_filter_apply', {
      catalogue, filter_count: tags.length,
      brand: this.brandSelect.value,
      model: this.modelSelect.value,
    });
    window.fpSkeleton?.grid();
    this.applying = true;
    window.location.href = next;
  }
}

if (!customElements.get('fp-dropdown')) {
  customElements.define('fp-dropdown', FpDropdown);
}

// The banner's "451 products" sits outside the section that a price or stock
// change redraws, so it kept the unfiltered count beside "253 items". It now
// follows the grid's own count (data-fp-result-count on <results-list>).
if (!window.fpResultCountWatch) {
  window.fpResultCountWatch = true;
  const fpSyncCount = () => {
    const label = document.querySelector('[data-fp-collection-count]');
    const count = Number(document.querySelector('results-list[data-fp-result-count]')?.getAttribute('data-fp-result-count'));
    if (!label || !Number.isFinite(count)) return;
    const text = `${count} ${count === 1 ? 'product' : 'products'}`;
    if (label.textContent !== text) label.textContent = text;
  };
  new MutationObserver(fpSyncCount).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['data-fp-result-count'],
  });
}

if (!customElements.get('fp-shop-filters')) {
  customElements.define('fp-shop-filters', FpShopFilters);
}

class FpShopPills extends HTMLElement {
  /** @type {ResizeObserver | undefined} */
  #resize;

  connectedCallback() {
    this.scroller = this.querySelector('.fp-shop-pills');
    this.prev = this.querySelector('[data-fp-pills-prev]');
    this.next = this.querySelector('[data-fp-pills-next]');
    if (!(this.scroller instanceof HTMLElement)) return;

    this.prev?.addEventListener('click', () => this.#move(-1));
    this.next?.addEventListener('click', () => this.#move(1));
    this.scroller.addEventListener('scroll', () => this.#sync(), { passive: true });
    this.#resize = new ResizeObserver(() => this.#sync());
    this.#resize.observe(this.scroller);
    this.#sync();
    this.#scrollActiveIntoView();
  }

  disconnectedCallback() {
    this.#resize?.disconnect();
  }

  #move(direction) {
    const amount = Math.max(180, Math.round(this.scroller.clientWidth * 0.7));
    this.scroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  #sync() {
    const max = this.scroller.scrollWidth - this.scroller.clientWidth;
    const overflow = max > 4;
    const left = this.scroller.scrollLeft;
    if (this.prev) this.prev.hidden = !overflow || left <= 4;
    if (this.next) this.next.hidden = !overflow || left >= max - 4;
    this.classList.toggle('is-start', overflow && left <= 4);
    this.classList.toggle('is-end', overflow && left >= max - 4);
    this.classList.toggle('is-middle', overflow && left > 4 && left < max - 4);
  }

  #scrollActiveIntoView() {
    const active = this.scroller.querySelector('.is-active, [aria-current="page"]');
    if (!(active instanceof HTMLElement)) return;
    const left = active.offsetLeft - 16;
    this.scroller.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
  }
}

if (!customElements.get('fp-shop-pills')) {
  customElements.define('fp-shop-pills', FpShopPills);
}
